import { ModelName } from '@casops/store/api';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import {
  columnDefAssetFilesApplied,
  columnDefAssetFilesStatus,
  columnDefModelActions,
} from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import {
  AssetFileDeviceModel,
  RegisteredAssetDeviceFileDeleteRequest,
  useDeleteAssetDeviceFileMutation,
} from '@casops/store/apis/registeredAssetsApi';
import { useParams } from 'react-router-dom';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';
import { EmptyCell } from '@casops/components/tables/formatters/genericFormatters';

type Model = AssetFileDeviceModel;

const modelName = ModelName.AssetDeviceFile;
const permission = Permission.Admin;

const getItemName = (data: Model): string => data.name;

export const useAssetDeviceFilesColumnDefs = (): ColDef<Model>[] => {
  const { t } = useTranslation();
  const { assetId } = useParams();
  const canDeleteItem = useCallback((data: Model): boolean => !!(assetId && data?.id), [assetId]);

  const getQueryArg = useCallback(
    (data: Model): RegisteredAssetDeviceFileDeleteRequest => ({
      id: Number(assetId),
      fileId: data.id,
    }),
    [assetId],
  );

  const cellRendererParams: ModelActionsCellRendererParams<
    Model,
    RegisteredAssetDeviceFileDeleteRequest
  > = useMemo(
    () => ({
      modelName,
      permission,
      deleteProps: {
        canDeleteItem,
        getQueryArg,
        useDeleteModelMutation: useDeleteAssetDeviceFileMutation,
        getItemName,
      },
    }),
    [canDeleteItem, getQueryArg],
  );

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>(EmptyCell);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.FileName') },
      { field: 'ver', headerName: t('Fields.FileVersion') },
      {
        field: 'rep',
        headerName: t('Fields.ReportTime'),
        ...absoluteTimeColDef,
      },
      {
        field: 'at',
        headerName: t('Fields.ActivationTime'),
        ...absoluteTimeColDef,
      },
      columnDefAssetFilesApplied(t),
      { ...columnDefAssetFilesStatus(t), suppressSizeToFit: true },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model, RegisteredAssetDeviceFileDeleteRequest>(
          t,
          cellRendererParams,
        ),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions, cellRendererParams, absoluteTimeColDef]);

  return columnDefs;
};

export default useAssetDeviceFilesColumnDefs;
